import Swiper from 'swiper';
import { A11y, Autoplay, EffectFade, Navigation, Pagination, Thumbs } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { DomHelper } from '../modules/DomHelper';

// Swiper CSS（必要なモジュールのみ）
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/thumbs';

/**
 * SwiperManager — 複数パターンのスライダー管理
 *
 * 使用例:
 *   SwiperManager.init('.js-swiper-hero', SwiperManager.presets.hero)
 *   SwiperManager.initAll() // data-swiper="preset名" で自動初期化
 *
 * HTML:
 *   <div class="swiper js-swiper" data-swiper="default">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *     <div class="swiper-pagination"></div>
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *   </div>
 */
export class SwiperManager {
  private instances: Map<string, Swiper> = new Map();

  // プリセット設定
  static readonly presets: Record<string, SwiperOptions> = {
    // デフォルト（シンプルなページネーション付き）
    default: {
      modules: [Pagination, A11y],
      loop: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: { enabled: true },
    },

    // ヒーロー（フェードエフェクト + 自動再生）
    hero: {
      modules: [Autoplay, Pagination, Navigation, EffectFade, A11y],
      effect: 'fade',
      loop: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      a11y: { enabled: true },
    },

    // カード（レスポンシブ複数表示）
    cards: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1.2,
      spaceBetween: 16,
      centeredSlides: true,
      breakpoints: {
        768: {
          slidesPerView: 2.5,
          spaceBetween: 24,
          centeredSlides: false,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 32,
          centeredSlides: false,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: { enabled: true },
    },

    // サムネイル付きメインスライダー（thumbs.swiper は initThumbPair() で注入）
    thumb: {
      modules: [Navigation, Thumbs, A11y],
      loop: true,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      thumbs: {},
      a11y: { enabled: true },
    },

    // 画像＋テキストスライダー（PC: 1.5列 center / SP: 1.2列 center）
    sliderMedia: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1.2,
      spaceBetween: 16,
      centeredSlides: true,
      loop: true,
      grabCursor: true,
      slideToClickedSlide: true,
      breakpoints: {
        1024: {
          slidesPerView: 1.5,
          spaceBetween: 40,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: { enabled: true },
    },

    // ニューススライダー（PC: 3.5列 center / SP: 1.2列 center）
    sliderNews: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1.2,
      spaceBetween: 16,
      centeredSlides: true,
      loop: true,
      grabCursor: true,
      slideToClickedSlide: true,
      breakpoints: {
        1024: {
          slidesPerView: 3.5,
          spaceBetween: 24,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: { enabled: true },
    },

    // ブランドスライダー（slidesPerView:'auto' / スライド幅 CSS 固定 / centeredSlides / フラクション・カスタムナビ）
    // スライド幅: SP:320px / TB:480px / PC:1120px（_brands.scss .swiper-slide で指定）
    // 対応 HTML: .c-slider__prev / .c-slider__next / .c-slider__fraction
    brands: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 'auto',
      spaceBetween: 0,
      centeredSlides: true,
      loop: false,
      navigation: {
        nextEl: '.c-slider__next',
        prevEl: '.c-slider__prev',
      },
      pagination: {
        el: '.c-slider__fraction',
        type: 'fraction',
        formatFractionCurrent: (n: number): string => String(n).padStart(2, '0'),
        formatFractionTotal: (n: number): string => String(n).padStart(2, '0'),
      },
      breakpoints: {
        768: { spaceBetween: 8 },
        1140: { spaceBetween: 16 },
      },
      a11y: { enabled: true },
    },

    // 導入事例スライダー（slidesPerView:'auto' / スライド幅 CSS 固定 / centeredSlides / フラクション・カスタムナビ）
    // スライド幅: SP:300px / TB:340px / PC:360px（_usecase-slider.scss .swiper-slide で指定）
    // 対応 HTML: .c-slider__prev / .c-slider__next / .c-slider__fraction
    usecase: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 'auto',
      spaceBetween: 0,
      centeredSlides: true,
      loop: false,
      navigation: {
        nextEl: '.c-slider__next',
        prevEl: '.c-slider__prev',
      },
      pagination: {
        el: '.c-slider__fraction',
        type: 'fraction',
        formatFractionCurrent: (n: number): string => String(n).padStart(2, '0'),
        formatFractionTotal: (n: number): string => String(n).padStart(2, '0'),
      },
      breakpoints: {
        768: { spaceBetween: 8 },
        1140: { spaceBetween: 16 },
      },
      a11y: { enabled: true },
    },

    // コラムスライダー（SP: USE CASE と同様 / PC: 3枚全表示・フラクション・カスタムナビ）
    // 対応 HTML: .c-slider__prev / .c-slider__next / .c-slider__fraction
    column: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 'auto',
      spaceBetween: 0,
      centeredSlides: true,
      loop: false,
      navigation: {
        nextEl: '.c-slider__next',
        prevEl: '.c-slider__prev',
      },
      pagination: {
        el: '.c-slider__fraction',
        type: 'fraction',
        formatFractionCurrent: (n: number): string => String(n).padStart(2, '0'),
        formatFractionTotal: (n: number): string => String(n).padStart(2, '0'),
      },
      breakpoints: {
        768: { spaceBetween: 8 },
        1140: { spaceBetween: 16, centeredSlides: false },
      },
      a11y: { enabled: true },
    },

    // ブランド詳細 USE CASE スライダー（slidesPerView:'auto' / スライド幅 CSS 固定 / column と同仕様）
    // スライド幅: SP:316px / TB:356px / PC:376px（_brand-usecase.scss .swiper-slide で指定）
    // 対応 HTML: .c-slider__prev / .c-slider__next / .c-slider__fraction
    brandUsecase: {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 'auto',
      spaceBetween: 0,
      centeredSlides: true,
      loop: false,
      navigation: {
        nextEl: '.c-slider__next',
        prevEl: '.c-slider__prev',
      },
      pagination: {
        el: '.c-slider__fraction',
        type: 'fraction',
        formatFractionCurrent: (n: number): string => String(n).padStart(2, '0'),
        formatFractionTotal: (n: number): string => String(n).padStart(2, '0'),
      },
      breakpoints: {
        768: { spaceBetween: 8 },
        1140: { spaceBetween: 16, centeredSlides: false },
      },
      a11y: { enabled: true },
    },

    // サムネイルナビゲーション側（watchSlidesProgress 必須）
    thumbNav: {
      modules: [Thumbs, A11y],
      loop: false,
      slidesPerView: 4,
      spaceBetween: 8,
      watchSlidesProgress: true,
      breakpoints: {
        768: { slidesPerView: 5, spaceBetween: 12 },
      },
      a11y: { enabled: true },
    },
  };

  /**
   * presets は static 参照のため直接渡すと Swiper が内部で変異させる。
   * navigation 要素をコンテナ内 → 親 section/article スコープ → グローバルの順で解決し、
   * 同一ページに複数スライダーがある場合でも正しい要素を取得できるようにする。
   */
  private static resolveEl(
    container: HTMLElement,
    selector: string | HTMLElement | null | undefined
  ): HTMLElement | string | null | undefined {
    if (typeof selector !== 'string') return selector;
    // 1. コンテナ内（nav がスワイパー内にある場合）
    const inContainer = container.querySelector<HTMLElement>(selector);
    if (inContainer) return inContainer;
    // 2. 最近傍の section / article / [data-slider-scope] スコープ内
    const scope = container.closest<HTMLElement>('section, article, [data-slider-scope]');
    if (scope) {
      const inScope = scope.querySelector<HTMLElement>(selector);
      if (inScope) return inScope;
    }
    // 3. フォールバック: グローバルセレクタ文字列をそのまま返す
    return selector;
  }

  private static resolveOptions(container: HTMLElement, options: SwiperOptions): SwiperOptions {
    const cloned: SwiperOptions = { ...options };

    if (cloned.navigation && typeof cloned.navigation === 'object') {
      const nav = cloned.navigation as {
        nextEl?: string | HTMLElement | null;
        prevEl?: string | HTMLElement | null;
      };
      cloned.navigation = {
        ...nav,
        nextEl: SwiperManager.resolveEl(container, nav.nextEl),
        prevEl: SwiperManager.resolveEl(container, nav.prevEl),
      };
    }

    if (cloned.pagination && typeof cloned.pagination === 'object') {
      const pag = cloned.pagination as { el?: string | HTMLElement | null };
      cloned.pagination = {
        ...(cloned.pagination as object),
        el: SwiperManager.resolveEl(container, pag.el),
      };
    }

    return cloned;
  }

  /** 単体初期化 */
  init(selector: string, options: SwiperOptions = SwiperManager.presets.default): Swiper | null {
    const el = DomHelper.qs<HTMLElement>(selector);
    if (!el) return null;

    const resolvedOptions = SwiperManager.resolveOptions(el, options);
    const instance = new Swiper(el, resolvedOptions);
    this.instances.set(selector, instance);
    return instance;
  }

  /** data-swiper 属性をもとに自動初期化 */
  initAll(selector = '.js-swiper'): void {
    DomHelper.qsa<HTMLElement>(selector).forEach((el) => {
      const presetName = el.dataset.swiper ?? 'default';
      const preset = SwiperManager.presets[presetName] ?? SwiperManager.presets.default;
      const key = `#${el.id || Math.random().toString(36).slice(2)}`;
      const resolvedOptions = SwiperManager.resolveOptions(el, preset);
      this.instances.set(key, new Swiper(el, resolvedOptions));
    });
  }

  /** インスタンス取得 */
  get(selector: string): Swiper | undefined {
    return this.instances.get(selector);
  }

  /** 全インスタンス破棄 */
  destroyAll(): void {
    this.instances.forEach((instance) => instance.destroy(true, true));
    this.instances.clear();
  }

  /** 静的ユーティリティ: 1行初期化 */
  static init(selector: string, options?: SwiperOptions): Swiper | null {
    const manager = new SwiperManager();
    return manager.init(selector, options);
  }

  /**
   * サムネイル連携ペアを初期化
   *
   * @param mainSelector  メインスライダーのセレクタ
   * @param thumbSelector サムネイルスライダーのセレクタ
   * @returns { main, thumb } Swiper インスタンスのペア、または null
   */
  static initThumbPair(
    mainSelector: string,
    thumbSelector: string
  ): { main: Swiper; thumb: Swiper } | null {
    const mainEl = DomHelper.qs<HTMLElement>(mainSelector);
    const thumbEl = DomHelper.qs<HTMLElement>(thumbSelector);
    if (!mainEl || !thumbEl) return null;

    // 1. サムネイル側を先に初期化
    const thumbInstance = new Swiper(thumbEl, { ...SwiperManager.presets.thumbNav });

    // 2. メイン側に thumbs.swiper を注入して初期化
    const mainInstance = new Swiper(mainEl, {
      ...SwiperManager.presets.thumb,
      thumbs: { swiper: thumbInstance },
    });

    return { main: mainInstance, thumb: thumbInstance };
  }
}
